/*!
 * Pricing/Tickets – section animation + countdown
 * - Frontend: GSAP + ScrollTrigger if available, else IO fallback
 * - Editor: runs once without ScrollTrigger
 * - No imports required; uses window.gsap / window.ScrollTrigger when present
 */
(function pricingSectionMount() {
  // ---------- tiny ready helper ----------
  const onReady = (fn) =>
    window.wp && wp.domReady
      ? wp.domReady(fn)
      : document.addEventListener("DOMContentLoaded", fn);

  // ---------- Countdown (kept from your version) ----------
  function initCountdown() {
    document
      .querySelectorAll(".pricing-countdown[data-end]")
      .forEach((wrap) => {
        const endMs = parseInt(wrap.getAttribute("data-end"), 10) * 1000;
        if (!endMs || isNaN(endMs)) return;

        const $d = wrap.querySelector('[data-unit="days"]');
        const $h = wrap.querySelector('[data-unit="hours"]');
        const $m = wrap.querySelector('[data-unit="minutes"]');
        const $s = wrap.querySelector('[data-unit="seconds"]');

        const pad = (n) => String(n).padStart(2, "0");

        function render() {
          const now = Date.now();
          const diff = Math.max(0, endMs - now);

          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor(diff / (1000 * 60 * 60)) % 24;
          const minutes = Math.floor(diff / (1000 * 60)) % 60;
          const seconds = Math.floor(diff / 1000) % 60;

          if ($d) $d.textContent = days;
          if ($h) $h.textContent = pad(hours);
          if ($m) $m.textContent = pad(minutes);
          if ($s) $s.textContent = pad(seconds);

          if (diff <= 0) clearInterval(timer);
        }

        render();
        const timer = setInterval(render, 1000);
      });
  }

  // ---------- GSAP path ----------
  function animateWithGSAP() {
    const gsap = window.gsap;
    if (!gsap) return false;

    const hasST = !!window.ScrollTrigger;
    if (hasST) gsap.registerPlugin(ScrollTrigger);

    const section = document.querySelector(".pricing");
    if (!section) return true;

    const isEditor = document.body.classList.contains("wp-admin");

    const lead = section.querySelector(".pricing__lead");
    const eyebrow = section.querySelector(".pricing__eyebrow");
    const dot = eyebrow ? eyebrow.querySelector(".dot") : null;
    const headline = section.querySelector(".pricing__headline");
    const intro = section.querySelector(".pricing__intro");
    const cta = section.querySelector(".pricing__cta");

    const cardsWrap = section.querySelector(".pricing__cards");
    const cards = gsap.utils.toArray(".pricing-card");
    const badges = gsap.utils.toArray(".pricing-card__badge");
    const prices = gsap.utils.toArray(".pricing-card .pricing-card__price");

    const countdown = section.querySelector(".pricing-countdown");

    // Set initial states (prevents layout jumps)
    if (eyebrow) gsap.set(eyebrow, { autoAlpha: 0, y: 18 });
    if (dot) gsap.set(dot, { scale: 0.6, transformOrigin: "50% 50%" });
    if (headline)
      gsap.set(headline, { autoAlpha: 0, y: 22, filter: "blur(2px)" });
    if (intro) gsap.set(intro, { autoAlpha: 0, y: 18 });
    if (cta) gsap.set(cta, { autoAlpha: 0, y: 18 });
    if (cards.length) gsap.set(cards, { autoAlpha: 0, y: 28, scale: 0.98 });
    if (badges.length)
      gsap.set(badges, {
        autoAlpha: 0,
        y: -6,
        scale: 0.96,
        transformOrigin: "50% 50%",
      });
    if (prices.length) gsap.set(prices, { autoAlpha: 0, y: 10 });
    if (countdown) gsap.set(countdown, { autoAlpha: 0, y: 16 });

    const tl = gsap.timeline({
      defaults: { ease: "power3.out" },
      ...(hasST && !isEditor
        ? {
            scrollTrigger: {
              trigger: section,
              start: "top 75%", // fire when the section is almost in view
              once: true,
            },
          }
        : {}),
    });

    // Lead column
    if (eyebrow) tl.to(eyebrow, { autoAlpha: 1, y: 0, duration: 0.4 });
    if (dot)
      tl.to(
        dot,
        { scale: 1, duration: 0.45, ease: "back.out(2)" },
        "<" // parallel with eyebrow
      );
    if (headline)
      tl.to(
        headline,
        { autoAlpha: 1, y: 0, filter: "blur(0px)", duration: 0.7 },
        "-=0.1"
      );
    if (intro) tl.to(intro, { autoAlpha: 1, y: 0, duration: 0.55 }, "-=0.25");
    if (cta) tl.to(cta, { autoAlpha: 1, y: 0, duration: 0.55 }, "-=0.25");

    // Cards
    if (cards.length) {
      tl.to(
        cards,
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          stagger: 0.12,
          ease: "power2.out",
        },
        "-=0.1"
      );

      // badge pop & price lift slightly after cards appear
      if (badges.length)
        tl.to(
          badges,
          {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            duration: 0.35,
            stagger: 0.08,
            ease: "back.out(2)",
          },
          "-=0.2"
        );

      if (prices.length)
        tl.to(
          prices,
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.35,
            stagger: 0.08,
            ease: "power2.out",
          },
          "-=0.25"
        );
    }

    // Countdown ribbon
    if (countdown)
      tl.to(countdown, { autoAlpha: 1, y: 0, duration: 0.45 }, "-=0.05");

    // Subtle float on featured CTA (non-blocking)
    const featuredBtn = section.querySelector(".pricing-card.is-featured .btn");
    if (featuredBtn && !isEditor) {
      gsap.to(featuredBtn, {
        y: -2,
        duration: 2.2,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }

    return true;
  }

  // ---------- IntersectionObserver fallback ----------
  function animateWithIO() {
    const section = document.querySelector(".pricing");
    if (!section) return;

    const leadBits = [
      ".pricing__eyebrow",
      ".pricing__headline",
      ".pricing__intro",
      ".pricing__cta",
    ]
      .map((s) => section.querySelector(s))
      .filter(Boolean);

    const groupA = leadBits;
    const groupB = Array.from(section.querySelectorAll(".pricing-card"));
    const badges = Array.from(section.querySelectorAll(".pricing-card__badge"));
    const prices = Array.from(
      section.querySelectorAll(".pricing-card .pricing-card__price")
    );
    const countdown = section.querySelector(".pricing-countdown");

    const setHidden = (el) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(16px)";
      el.style.willChange = "transform, opacity";
    };

    groupA.forEach(setHidden);
    groupB.forEach(setHidden);
    badges.forEach((b) => {
      b.style.opacity = "0";
      b.style.transform = "translateY(-4px) scale(0.96)";
      b.style.willChange = "transform, opacity";
    });
    prices.forEach(setHidden);
    if (countdown) setHidden(countdown);

    const show = (els, delayStagger = 0) => {
      els.forEach((el, i) => {
        el.style.transition = "opacity .55s ease, transform .55s ease";
        el.style.transitionDelay = `${i * delayStagger}s`;
        requestAnimationFrame(() => {
          el.style.opacity = "1";
          el.style.transform = "translateY(0)";
        });
      });
    };

    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;

          // Lead bits together
          if (groupA.includes(e.target)) {
            show(groupA, 0.05);
            groupA.length = 0;
          }

          // Cards as a group
          if (groupB.includes(e.target)) {
            show(groupB, 0.08);
            // badges slightly delayed
            setTimeout(() => show(badges, 0.06), 120);
            // prices slightly delayed
            setTimeout(() => show(prices, 0.06), 120);
            groupB.length = 0;
          }

          if (countdown && e.target === countdown) {
            show([countdown], 0);
          }

          obs.unobserve(e.target);
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 }
    );

    [...groupA, ...groupB, ...badges, ...prices, countdown].forEach((el) => {
      if (el) io.observe(el);
    });
  }

  // ---------- boot ----------
  onReady(function () {
    initCountdown();

    // GSAP path (with editor support), else IO fallback
    const ok = animateWithGSAP();
    if (!ok) animateWithIO();
  });
})();
